import { useQueryClient } from "@tanstack/react-query";

import {
	type ChatbotDto,
	getChatbotControllerGetMessagesV1QueryKey,
} from "@virtual-brain-lib/frontend-api";
import {
	ChatInput,
	ChatMessagesWrapper,
	Message,
	MessageAssistantLoading,
	prependChatbotInitialMessage,
	useChatbotMessages,
	useChatStore,
} from "@virtual-brain-lib/frontend-design-system/components";
import { Button, Spinner } from "@virtual-brain-lib/frontend-design-system/ui";
import type { ChatbotSession } from "@virtual-brain-lib/types";

import { clearChatbotSessionId } from "../../utils/storage";
import { ChatbotAppMessageActions } from "./ChatbotAppMessageActions";

interface ChatbotAppChatMessagesProps {
	readonly chatbot: ChatbotDto;
	readonly chatbotSessionId?: ChatbotSession["id"];
}

export default function ChatbotAppChatMessages({
	chatbot,
	chatbotSessionId,
}: ChatbotAppChatMessagesProps) {
	const queryClient = useQueryClient();

	const inputValue = useChatStore((store) => store.inputValue);
	const isAnyMessageAnswering = useChatStore((store) => store.answeringByMessageId.size > 0);

	const { messages, isLoading, sendMessage, updateCachedMessage } = useChatbotMessages({
		chatbot,
		chatbotSessionId,
	});

	async function handleFollowUp(message: string) {
		await sendMessage(message);
	}

	function clearHistory() {
		clearChatbotSessionId(chatbot.id);

		queryClient.resetQueries({
			queryKey: getChatbotControllerGetMessagesV1QueryKey(chatbot.id),
		});
	}

	if (isLoading) {
		return (
			<div className="flex h-full w-full items-center justify-center">
				<Spinner size="sm" />
			</div>
		);
	}

	return (
		<>
			<ChatMessagesWrapper className="gap-6 pb-20">
				{prependChatbotInitialMessage(messages, chatbot).map((message, index) => (
					<Message
						key={message.id}
						chatId={chatbotSessionId ?? ""}
						message={message}
						isLastMessage={index === messages.length}
						thinkingComponent={(streamedMessage) =>
							streamedMessage.isThinking && streamedMessage.role === "assistant" ? (
								<MessageAssistantLoading className="mt-2" />
							) : undefined
						}
						actionsComponent={
							chatbot.displaySources
								? (streamedMessage) => (
										<ChatbotAppMessageActions message={streamedMessage} />
									)
								: undefined
						}
						updateCachedMessage={(messageId, updates) => {
							updateCachedMessage({ messageId, updates });
						}}
						onRetry={async (content) => sendMessage(content)}
						onFollowUp={handleFollowUp}
					/>
				))}
			</ChatMessagesWrapper>
			<div className="mx-4 mb-4 flex flex-col items-center justify-center gap-1">
				<ChatInput
					className="w-full max-w-2xl rounded-md bg-background py-1"
					textareaClassName="min-h-0"
					isDisabled={isAnyMessageAnswering || inputValue.trim() === ""}
					placeholder="Ask me a question"
					textareaEndComponent={
						<Button
							type="submit"
							leftIcon="paperPlane"
							variant="ghost"
							disabled={isAnyMessageAnswering || inputValue.trim() === ""}
						/>
					}
					onSubmit={async (value) => sendMessage(value)}
				/>

				<div className="mx-2 mt-2 flex justify-center gap-2 text-center text-xs text-gray-600">
					Powered by
					<a
						href="https://getvirtualbrain.com"
						target="_blank"
						rel="noreferrer"
						className="underline"
					>
						VirtualBrain
					</a>
					|
					<div className="cursor-pointer underline" onClick={clearHistory}>
						Clear history
					</div>
				</div>
			</div>
		</>
	);
}
